import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { CodeBlock } from '@core/services/ai/ai-assistant.service';

@Component({
  selector: 'app-ai-code-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule],
  template: `
    <div class="cb">
      <header class="cb__head">
        <div class="cb__head-left">
          @if (block.title) {
            <span class="cb__title">{{ block.title }}</span>
          }
          @if (block.language) {
            <span class="cb__lang">{{ block.language }}</span>
          }
        </div>
        <button
          type="button"
          mat-stroked-button
          class="cb__copy"
          (click)="copy()">
          <mat-icon>{{ copied ? 'check' : 'content_copy' }}</mat-icon>
          <span>{{ copied ? label('aiAssistant.widgets.copied', 'Copied') : label('aiAssistant.widgets.copy', 'Copy') }}</span>
        </button>
      </header>
      <pre class="cb__body"><code>{{ block.code }}</code></pre>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .cb {
        background: #0f172a;
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid rgba(15, 23, 42, 0.08);
      }
      .cb__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 8px 12px;
        background: rgba(255, 255, 255, 0.04);
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      }
      .cb__head-left {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
      }
      .cb__title {
        color: rgba(255, 255, 255, 0.85);
        font-size: 13px;
        font-weight: 600;
        overflow-wrap: anywhere;
      }
      .cb__lang {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
        font-size: 11px;
        padding: 2px 6px;
        border-radius: 4px;
        background: rgba(99, 102, 241, 0.25);
        color: #c7d2fe;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .cb__copy.mat-mdc-button-base {
        color: rgba(255, 255, 255, 0.85);
        border-color: rgba(255, 255, 255, 0.18);
        min-width: auto;
        padding: 0 10px;
        gap: 4px;
      }
      .cb__copy mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
      .cb__body {
        margin: 0;
        padding: 12px 14px;
        overflow-x: auto;
        max-width: 100%;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
        font-size: 12px;
        line-height: 1.5;
        color: #e2e8f0;
        white-space: pre;
      }
      .cb__body code {
        background: transparent;
        color: inherit;
        padding: 0;
        font-size: inherit;
      }
    `
  ]
})
export class AiCodeBlockComponent {
  @Input({ required: true }) block!: CodeBlock;
  copied = false;

  private readonly translate = inject(TranslateService, { optional: true });

  /** Translated label; falls back to English when no translation service/key is available. */
  protected label(key: string, fallback: string): string {
    const v = this.translate?.instant(key);
    return v && v !== key ? v : fallback;
  }

  async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.block.code);
      this.copied = true;
      setTimeout(() => (this.copied = false), 1500);
    } catch {
      // best-effort
    }
  }
}
